import { useCallback, useEffect, useMemo, useState } from 'react';

import { IBeforeInstallPromptEvent, IPwaInstallState, PwaInstallPromptOutcome } from '../types';
import { detectPlatform, isMobileUserAgent } from '../utils/detectPlatform';
import { readStandaloneDisplayModeFromWindow } from '../utils/isStandaloneDisplayMode';

export interface IUsePwaInstallOptions {
    win?: Window;
}

export interface IUsePwaInstallResult extends IPwaInstallState {
    showInstallInstructions: boolean;
    openInstallInstructions: () => void;
    closeInstallInstructions: () => void;
    /**
     * Starts the best available install path for the current platform.
     * - Android + deferred prompt → native install sheet
     * - otherwise → in-app install guide (tabs + steps)
     */
    startInstallFlow: () => Promise<PwaInstallPromptOutcome>;
}

/**
 * Install UX stays visible on mobile until the app is running standalone.
 * No dismiss / localStorage hide — product requirement: always nudge until installed.
 */
export function usePwaInstall(options: IUsePwaInstallOptions = {}): IUsePwaInstallResult {
    const win = options.win ?? window;

    const [deferredPrompt, setDeferredPrompt] = useState<IBeforeInstallPromptEvent | null>(null);
    const [showInstallInstructions, setShowInstallInstructions] = useState(false);
    const [isStandalone, setIsStandalone] = useState(() => readStandaloneDisplayModeFromWindow(win));

    const platform = useMemo(
        () =>
            detectPlatform({
                userAgent: win.navigator.userAgent,
                maxTouchPoints: win.navigator.maxTouchPoints,
                platform: win.navigator.platform,
            }),
        [win]
    );

    const isMobile = useMemo(
        () => isMobileUserAgent(win.navigator.userAgent, win.navigator.maxTouchPoints, win.navigator.platform),
        [win]
    );

    useEffect(() => {
        const syncStandalone = () => setIsStandalone(readStandaloneDisplayModeFromWindow(win));
        const media = win.matchMedia?.('(display-mode: standalone)');

        syncStandalone();

        const onBeforeInstallPrompt = (event: Event) => {
            event.preventDefault();
            setDeferredPrompt(event as IBeforeInstallPromptEvent);
        };

        const onAppInstalled = () => {
            setDeferredPrompt(null);
            setIsStandalone(true);
            setShowInstallInstructions(false);
            // Fresh install: allow soft push prompt / re-subscribe even if old markers remain
            // (PWA uninstall often does not clear origin localStorage).
            try {
                const keys = Object.keys(win.localStorage);
                keys.forEach((key) => {
                    if (key.startsWith('pwa.push.promptDismissed.') || key.startsWith('pwa.push.synced.')) {
                        win.localStorage.removeItem(key);
                    }
                    if (key.startsWith('pwa.push.sessionSync.')) {
                        win.sessionStorage?.removeItem(key);
                    }
                });
            } catch {
                // ignore
            }
        };

        win.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
        win.addEventListener('appinstalled', onAppInstalled);
        media?.addEventListener?.('change', syncStandalone);

        return () => {
            win.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
            win.removeEventListener('appinstalled', onAppInstalled);
            media?.removeEventListener?.('change', syncStandalone);
        };
    }, [win]);

    const canPromptNativeInstall = Boolean(deferredPrompt) && !isStandalone;
    const shouldShowInstallUi = isMobile && !isStandalone && (platform === 'ios' || platform === 'android');

    const openInstallInstructions = useCallback(() => setShowInstallInstructions(true), []);
    const closeInstallInstructions = useCallback(() => setShowInstallInstructions(false), []);

    const startInstallFlow = useCallback(async (): Promise<PwaInstallPromptOutcome> => {
        if (canPromptNativeInstall && deferredPrompt) {
            await deferredPrompt.prompt();
            const choice = await deferredPrompt.userChoice;
            setDeferredPrompt(null);
            if (choice.outcome === 'accepted') {
                setShowInstallInstructions(false);
            }
            return choice.outcome;
        }

        setShowInstallInstructions(true);
        return 'instructions';
    }, [canPromptNativeInstall, deferredPrompt]);

    return {
        isStandalone,
        isMobile,
        platform,
        canPromptNativeInstall,
        shouldShowInstallUi,
        showInstallInstructions,
        openInstallInstructions,
        closeInstallInstructions,
        startInstallFlow,
    };
}
